/**
 * @fileoverview Subscription utilities for managing callback sets.
 * Provides a reusable pattern for pub/sub within modules.
 */

import { logger } from "./logger";

/**
 * A subscription manager for typed callbacks.
 */
export interface Subscription<T extends (...args: never[]) => void> {
  /** Add a callback and return unsubscribe function */
  subscribe: (callback: T) => () => void;
  /** Notify all subscribers with given arguments */
  notify: (...args: Parameters<T>) => void;
  /** Clear all subscribers */
  clear: () => void;
  /** Get current subscriber count */
  size: () => number;
}

/**
 * Create a subscription manager for callbacks.
 * Handles subscribe/unsubscribe and safe notification.
 *
 * @example
 * ```typescript
 * const onThemeChange = createSubscription<(theme: string) => void>();
 *
 * const unsubscribe = onThemeChange.subscribe((theme) => {
 *   console.log('Theme:', theme);
 * });
 *
 * onThemeChange.notify('dark');
 * unsubscribe();
 * ```
 */
export function createSubscription<
  T extends (...args: never[]) => void,
>(): Subscription<T> {
  const callbacks = new Set<T>();

  return {
    subscribe(callback: T): () => void {
      callbacks.add(callback);
      return () => {
        callbacks.delete(callback);
      };
    },

    notify(...args: Parameters<T>): void {
      callbacks.forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          logger.error("Error in subscription callback:", error);
        }
      });
    },

    clear(): void {
      callbacks.clear();
    },

    size(): number {
      return callbacks.size;
    },
  };
}
